/**
 * ProviderStatusCards 组件
 *
 * 显示所有搜索供应商的状态卡片，包括健康状态、密钥数量等。
 */

import React from 'react';
import { StatusBadge } from '../shared';
import type { SearchProviderStats } from '../../../services/searchAdminApi';

interface ProviderStatusCardsProps {
  providers: SearchProviderStats[];
  loading: boolean;
}

const providerTypeLabels: Record<string, string> = {
  bailian: '阿里云百练',
  tavily: 'Tavily',
  baidu: '百度千帆',
  shuqi: '书旗小说',
};

export const ProviderStatusCards: React.FC<ProviderStatusCardsProps> = ({
  providers,
  loading,
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
          >
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">暂无供应商数据</div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {providers.map((provider) => {
        const hasOpenCircuit = provider.circuit_breakers.some(
          (cb) => cb.state === 'open'
        );
        const isHealthy =
          provider.enabled &&
          provider.total_failures < 5 &&
          !hasOpenCircuit;

        return (
          <div
            key={provider.provider_name}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-800 truncate">
                {provider.display_name ||
                  providerTypeLabels[provider.provider_name] ||
                  provider.provider_name}
              </h3>
              <StatusBadge
                variant={isHealthy ? 'success' : provider.enabled ? 'warning' : 'neutral'}
                size="sm"
                dot
                pulse={hasOpenCircuit}
              >
                {provider.enabled ? (isHealthy ? '健康' : '异常') : '禁用'}
              </StatusBadge>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">密钥</span>
                <span className="font-medium">
                  {provider.enabled_key_count}/{provider.key_count}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">优先级</span>
                <span className="font-medium">{provider.priority}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">平均延迟</span>
                <span
                  className={`font-medium ${
                    provider.avg_latency_ms > 3000
                      ? 'text-red-600'
                      : provider.avg_latency_ms > 1000
                        ? 'text-yellow-600'
                        : 'text-green-600'
                  }`}
                >
                  {provider.avg_latency_ms > 0
                    ? `${Math.round(provider.avg_latency_ms)}ms`
                    : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">检查/失败</span>
                <span className="font-medium">
                  {provider.total_checks}/{provider.total_failures}
                </span>
              </div>
            </div>

            {hasOpenCircuit && (
              <div className="mt-2 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                熔断器已开启 ({provider.circuit_breakers.filter((cb) => cb.state === 'open').length})
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProviderStatusCards;
